import React, { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../contexts/AdminContext';
import {
  Sparkles,
  Calendar,
  Pencil,
  Trash2,
  Plus,
  X,
  AlertTriangle,
  ExternalLink,
  Loader2,
  Save,
} from 'lucide-react';

// ── API base (Vite) ──
const API_BASE =
  import.meta.env.VITE_BACKEND_URL ||
  (typeof window !== 'undefined' ? window.location.origin : '');

// Cores por tipo de item (badge interno da versão)
const ITEM_TYPES = {
  correcao: { label: 'CORREÇÃO', bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.3)', color: '#FCA5A5' },
  melhoria: { label: 'MELHORIA', bg: 'rgba(13,174,255,0.12)', border: 'rgba(13,174,255,0.3)', color: '#7DD3FC' },
  novo: { label: 'NOVO', bg: 'rgba(52,211,153,0.12)', border: 'rgba(52,211,153,0.3)', color: '#6EE7B7' },
  atualizacao: { label: 'ATUALIZAÇÃO', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)', color: '#FCD34D' },
};

const formatDatePtBr = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
};

const labelStyle = {
  fontSize: '12px', fontWeight: 500, color: 'var(--dp-text-secondary)',
  display: 'block', marginBottom: '4px',
};

// ══════════════════════════════════════════════════════════════════════════════
// MODAL — Criar / Editar versão
// ══════════════════════════════════════════════════════════════════════════════
const PlatformUpdateModal = ({ update, onClose, onSaved }) => {
  const isEdit = !!update;
  const [form, setForm] = useState({
    version: update?.version || '',
    release_date: update?.release_date || new Date().toISOString().slice(0, 10),
    is_recent: !!update?.is_recent,
    action_box_enabled: !!update?.action_box_enabled,
    action_box_title: update?.action_box_title || '',
    action_box_subtitle: update?.action_box_subtitle || '',
    action_box_version_label: update?.action_box_version_label || '',
    action_box_button_text: update?.action_box_button_text || 'Atualizar',
    action_box_button_url: update?.action_box_button_url || '',
  });
  const [items, setItems] = useState(
    update?.items?.length
      ? update.items.map((it) => ({ type: it.type || 'novo', description: it.description || '', link_url: it.link_url || '' }))
      : [{ type: 'novo', description: '', link_url: '' }]
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateItem = (idx, patch) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));
  const addItem = () => setItems((arr) => [...arr, { type: 'novo', description: '', link_url: '' }]);

  const save = async () => {
    if (!form.version.trim()) { setError('O título é obrigatório.'); return; }
    if (!form.release_date) { setError('A data é obrigatória.'); return; }
    setSaving(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const cleanItems = items
        .map((it) => ({ type: it.type, description: (it.description || '').trim(), link_url: (it.link_url || '').trim() || null }))
        .filter((it) => it.description);
      const body = { ...form, items: cleanItems };
      const url = isEdit
        ? `${API_BASE}/api/platform-updates/${update.id}`
        : `${API_BASE}/api/platform-updates`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const saved = await res.json();
      onSaved(saved);
    } catch (e) {
      setError(`Erro ao salvar: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
        animation: 'dp-pageFadeIn 0.2s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="dp-thin-scrollbar"
        style={{
          background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-medium)',
          borderRadius: 'var(--dp-radius-lg, 16px)', width: '100%', maxWidth: '600px',
          maxHeight: '85vh', overflowY: 'auto', padding: '20px', fontFamily: 'var(--dp-font)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--dp-text-primary)' }}>
            {isEdit ? `Editar: ${update.version}` : 'Nova versão'}
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--dp-text-tertiary)', display: 'flex' }}>
            <X size={18} />
          </button>
        </div>

        {/* Informações da versão */}
        <div style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.8px', color: 'var(--dp-text-tertiary)', textTransform: 'uppercase', marginBottom: '8px' }}>
          Informações da versão
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
          <div>
            <label style={labelStyle}>Título *</label>
            <input className="dp-input" value={form.version} onChange={(e) => set('version', e.target.value)} placeholder="Ex: vSpeech, Atualização do App, Novo recurso, etc." />
          </div>
          <div>
            <label style={labelStyle}>Data de lançamento *</label>
            <input className="dp-input" type="date" value={form.release_date} onChange={(e) => set('release_date', e.target.value)} />
          </div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '20px', fontSize: '13px', color: 'var(--dp-text-primary)' }}>
          <input type="checkbox" checked={form.is_recent} onChange={(e) => set('is_recent', e.target.checked)} />
          Marcar como recente (exibe badge "RECENTE")
        </label>

        {/* Mudanças */}
        <div style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.8px', color: 'var(--dp-text-tertiary)', textTransform: 'uppercase', marginBottom: '8px' }}>
          Mudanças
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '10px' }}>
          {items.map((it, i) => {
            const meta = ITEM_TYPES[it.type] || ITEM_TYPES.novo;
            return (
              <div key={i} style={{ background: 'var(--dp-bg-secondary)', border: '1px solid var(--dp-border-subtle)', borderRadius: '8px', padding: '10px' }}>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '8px' }}>
                  <select
                    className="dp-input"
                    value={it.type}
                    onChange={(e) => updateItem(i, { type: e.target.value })}
                    style={{ flex: '0 0 150px', borderLeft: `3px solid ${meta.color}` }}
                  >
                    <option value="correcao">Correção</option>
                    <option value="melhoria">Melhoria</option>
                    <option value="novo">Novo</option>
                    <option value="atualizacao">Atualização</option>
                  </select>
                  <button
                    onClick={() => removeItem(i)}
                    title="Remover mudança"
                    style={{ background: 'transparent', border: '1px solid var(--dp-border-subtle)', borderRadius: '6px', cursor: 'pointer', color: 'var(--dp-text-tertiary)', padding: '8px', display: 'flex', marginLeft: 'auto', boxShadow: 'none', transform: 'none' }}
                  >
                    <X size={14} />
                  </button>
                </div>
                <textarea className="dp-input" value={it.description} onChange={(e) => updateItem(i, { description: e.target.value })} placeholder="Descrição da mudança..." rows={2} style={{ resize: 'vertical', marginBottom: '6px' }} />
                <input className="dp-input" value={it.link_url} onChange={(e) => updateItem(i, { link_url: e.target.value })} placeholder="Link opcional (URL)..." />
              </div>
            );
          })}
        </div>
        <button
          onClick={addItem}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'transparent', border: '1px dashed rgba(13,174,255,0.4)', borderRadius: '8px', color: 'var(--dp-blue-500)', fontSize: '12px', fontWeight: 500, padding: '6px 12px', cursor: 'pointer', boxShadow: 'none', transform: 'none', marginBottom: '20px' }}
        >
          <Plus size={14} /> Adicionar mudança
        </button>

        {/* Box de ação opcional */}
        <div style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.8px', color: 'var(--dp-text-tertiary)', textTransform: 'uppercase', marginBottom: '8px' }}>
          Box de ação (opcional)
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: form.action_box_enabled ? '12px' : '0', fontSize: '13px', color: 'var(--dp-text-primary)' }}>
          <input type="checkbox" checked={form.action_box_enabled} onChange={(e) => set('action_box_enabled', e.target.checked)} />
          Adicionar box de ação destacado a esta versão
        </label>
        {form.action_box_enabled && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '12px', background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '8px' }}>
            <div>
              <label style={labelStyle}>Título</label>
              <input className="dp-input" value={form.action_box_title} onChange={(e) => set('action_box_title', e.target.value)} placeholder='ex: "Atualização da extensão necessária"' />
            </div>
            <div>
              <label style={labelStyle}>Subtítulo</label>
              <input className="dp-input" value={form.action_box_subtitle} onChange={(e) => set('action_box_subtitle', e.target.value)} placeholder='ex: "Atualize para usar todos os recursos"' />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div>
                <label style={labelStyle}>Label de versão</label>
                <input className="dp-input" value={form.action_box_version_label} onChange={(e) => set('action_box_version_label', e.target.value)} placeholder="ex: v1.0.29" />
              </div>
              <div>
                <label style={labelStyle}>Texto do botão</label>
                <input className="dp-input" value={form.action_box_button_text} onChange={(e) => set('action_box_button_text', e.target.value)} placeholder="Atualizar" />
              </div>
            </div>
            <div>
              <label style={labelStyle}>URL do botão</label>
              <input className="dp-input" value={form.action_box_button_url} onChange={(e) => set('action_box_button_url', e.target.value)} placeholder="https://..." />
            </div>
          </div>
        )}

        {error && <div style={{ fontSize: '12px', color: 'var(--dp-red-500)', marginTop: '12px' }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '18px' }}>
          <button
            onClick={onClose}
            style={{ padding: '8px 16px', borderRadius: 'var(--dp-radius-sm, 8px)', border: '1px solid var(--dp-border-medium)', background: 'transparent', color: 'var(--dp-text-secondary)', fontSize: '13px', fontWeight: 500, cursor: 'pointer', boxShadow: 'none', transform: 'none' }}
          >
            Cancelar
          </button>
          <button
            onClick={save}
            disabled={saving}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #0DAEFF, #3B82F6)', color: '#fff', fontSize: '13px', fontWeight: 600, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1, fontFamily: 'var(--dp-font)' }}
          >
            {saving ? <Loader2 size={14} className="dp-spin" /> : <Save size={14} />}
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// PANEL — Novidades da Plataforma (Dashboard)
// ══════════════════════════════════════════════════════════════════════════════
const PlatformUpdatesPanel = () => {
  const { isSuperAdmin } = useAdmin();
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUpdate, setEditingUpdate] = useState(null);

  const loadUpdates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/platform-updates`);
      const data = res.ok ? await res.json() : [];
      setUpdates(Array.isArray(data) ? data : []);
    } catch {
      setUpdates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUpdates(); }, [loadUpdates]);

  const openCreate = () => { setEditingUpdate(null); setModalOpen(true); };
  const openEdit = (u) => { setEditingUpdate(u); setModalOpen(true); };
  const handleSaved = async () => { setModalOpen(false); setEditingUpdate(null); await loadUpdates(); };

  const handleDelete = async (u) => {
    if (typeof window !== 'undefined' && !window.confirm(`Excluir "${u.version}"? Esta ação não pode ser desfeita.`)) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/platform-updates/${u.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await loadUpdates();
    } catch (e) {
      window.alert(`Erro ao excluir: ${e.message}`);
    }
  };

  const latestDate = updates[0]?.release_date ? formatDatePtBr(updates[0].release_date) : null;

  const iconBtnStyle = {
    width: '28px', height: '28px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(255,255,255,0.04)', border: 'none', borderRadius: '6px',
    cursor: 'pointer', color: '#9CA3AF', boxShadow: 'none', transform: 'none', flexShrink: 0,
    transition: '0.2s ease',
  };

  return (
    <div className="dp-top-accent" style={{
      position: 'relative', overflow: 'hidden',
      background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)',
      borderRadius: 'var(--dp-radius-md, 12px)',
      fontFamily: 'var(--dp-font)',
      display: 'flex', flexDirection: 'column',
      // CRUCIAL: height:100% pra encher a grid cell; minHeight:0 sobrescreve
      // o min-height:auto default dos grid items (que travaria em max-content)
      height: '100%', minHeight: 0,
    }}>
      {/* Barra de degradê contínua no topo */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
        background: 'linear-gradient(90deg, #0DAEFF, #7C3AED, #EC4899)',
      }} />

      {/* Header — padding 16/20 + min-height 56 + box-sizing border-box pra
          que a border-bottom alinhe com a do header do Downloads. Icone inline
          (sem container) pra manter content-height baixo (Sparkles 18px) e
          deixar o min-height dominar. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        padding: '16px 20px', minHeight: '56px',
        borderBottom: '1px solid var(--dp-border-subtle)',
        boxSizing: 'border-box', flexShrink: 0,
      }}>
        <Sparkles size={18} style={{ color: '#A78BFA', flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--dp-text-primary)' }}>Novidades da Plataforma</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '9px', fontWeight: 700, color: 'var(--dp-green-500)', background: 'var(--dp-green-bg)', border: '1px solid rgba(52,211,153,0.3)', padding: '3px 8px', borderRadius: '999px' }}>
          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--dp-green-500)', animation: 'dp-pulse-dot 1.5s ease-in-out infinite' }} />
          AO VIVO
        </div>
      </div>

      {/* Lista de versões */}
      <div
        className="dp-thin-scrollbar"
        style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px 20px 4px 20px', marginRight: '-4px', paddingRight: '8px' }}
      >
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--dp-text-tertiary)', fontSize: '13px', padding: '16px 0' }}>
            <Loader2 size={16} className="dp-spin" /> Carregando novidades...
          </div>
        ) : updates.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <Sparkles size={32} style={{ color: '#A78BFA', opacity: 0.5 }} />
            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--dp-text-primary)', marginTop: '10px' }}>Nenhuma novidade ainda</div>
            <p style={{ fontSize: '11px', fontWeight: 400, color: 'var(--dp-text-secondary)', maxWidth: '320px', margin: '6px auto 12px', lineHeight: 1.5 }}>
              {isSuperAdmin ? 'Crie a primeira versão pra anunciar mudanças aos usuários.' : 'Em breve teremos atualizações pra mostrar aqui.'}
            </p>
            {isSuperAdmin && (
              <button
                onClick={openCreate}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #0DAEFF, #3B82F6)', color: '#fff', fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--dp-font)' }}
              >
                <Plus size={14} /> Criar primeira versão
              </button>
            )}
          </div>
        ) : (
          <div style={{ position: 'relative', paddingLeft: '18px' }}>
            {/* Timeline lateral (linha tracejada) */}
            <div style={{ position: 'absolute', top: '4px', bottom: '4px', left: '6px', borderLeft: '1px dashed rgba(255,255,255,0.1)' }} />

            {updates.map((u) => {
              const meta = u.is_recent
                ? { bg: 'rgba(13,174,255,0.03)', border: 'rgba(13,174,255,0.3)' }
                : { bg: 'var(--dp-bg-secondary)', border: 'var(--dp-border-subtle)' };

              return (
                <div key={u.id} style={{ position: 'relative', marginBottom: '14px' }}>
                  {/* Dot da timeline */}
                  <div style={{
                    position: 'absolute', left: '-18px', top: '14px',
                    width: '12px', height: '12px', borderRadius: '50%',
                    background: u.is_recent ? '#0DAEFF' : 'var(--dp-bg-card)',
                    border: u.is_recent ? '2px solid #0DAEFF' : '2px solid rgba(255,255,255,0.15)',
                    boxShadow: u.is_recent ? '0 0 0 4px rgba(13,174,255,0.15)' : 'none',
                  }} />

                  <div style={{
                    background: meta.bg, border: `1px solid ${meta.border}`,
                    borderRadius: '10px', padding: '12px 14px',
                  }}>
                    {/* Topo: [título + RECENTE] à esquerda, [data + admin] à direita */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
                      {/* Esquerda: título + badge RECENTE */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--dp-text-primary)' }}>{u.version}</span>
                        {u.is_recent && (
                          <span style={{
                            fontSize: '9px', fontWeight: 700, letterSpacing: '0.5px',
                            color: '#fff', background: 'linear-gradient(135deg, #0DAEFF, #3B82F6)',
                            padding: '2px 8px', borderRadius: '999px',
                          }}>RECENTE</span>
                        )}
                      </div>
                      {/* Direita: data + (se super admin) botões editar/excluir */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10px', fontWeight: 400, color: 'var(--dp-text-tertiary)' }}>
                          <Calendar size={11} />
                          {formatDatePtBr(u.release_date)}
                        </span>
                        {isSuperAdmin && (
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <button
                              title="Editar"
                              onClick={() => openEdit(u)}
                              style={iconBtnStyle}
                              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                            ><Pencil size={13} /></button>
                            <button
                              title="Excluir"
                              onClick={() => handleDelete(u)}
                              style={iconBtnStyle}
                              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#EF4444'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.color = '#9CA3AF'; }}
                            ><Trash2 size={13} /></button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Items */}
                    {Array.isArray(u.items) && u.items.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {u.items.map((it, idx) => {
                          const m = ITEM_TYPES[it.type] || ITEM_TYPES.novo;
                          return (
                            <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                              <span style={{
                                flexShrink: 0, fontSize: '9px', fontWeight: 700, letterSpacing: '0.4px',
                                color: m.color, background: m.bg, border: `1px solid ${m.border}`,
                                padding: '2px 6px', borderRadius: '4px', marginTop: '1px', whiteSpace: 'nowrap',
                              }}>{m.label}</span>
                              <div style={{ fontSize: '11px', fontWeight: 400, color: 'var(--dp-text-secondary)', lineHeight: 1.5, flex: 1, minWidth: 0 }}>
                                {it.description}
                                {it.link_url && (
                                  <>
                                    {' '}
                                    <a
                                      href={it.link_url} target="_blank" rel="noopener noreferrer"
                                      style={{ color: '#0DAEFF', textDecoration: 'none', fontSize: '11px', fontWeight: 500 }}
                                      onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                                      onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                                    >
                                      Ver detalhes →
                                    </a>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Action box opcional */}
                    {u.action_box_enabled && (u.action_box_title || u.action_box_button_url) && (
                      <div style={{
                        display: 'flex', alignItems: 'flex-start', gap: '10px',
                        marginTop: '12px', padding: '10px 12px',
                        background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)',
                        borderRadius: '8px',
                      }}>
                        <AlertTriangle size={16} style={{ color: '#F59E0B', flexShrink: 0, marginTop: '2px' }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {u.action_box_title && (
                            <div style={{ fontSize: '12.5px', fontWeight: 600, color: '#FDE68A', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              {u.action_box_title}
                              {u.action_box_version_label && (
                                <span style={{ fontSize: '10px', fontWeight: 600, color: '#FCD34D', background: 'rgba(245,158,11,0.15)', padding: '1px 6px', borderRadius: '4px' }}>
                                  {u.action_box_version_label}
                                </span>
                              )}
                            </div>
                          )}
                          {u.action_box_subtitle && (
                            <div style={{ fontSize: '12px', color: 'var(--dp-text-secondary)', marginTop: '2px', lineHeight: 1.5 }}>
                              {u.action_box_subtitle}
                            </div>
                          )}
                        </div>
                        {u.action_box_button_url && (
                          <a
                            href={u.action_box_button_url} target="_blank" rel="noopener noreferrer"
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: '5px',
                              padding: '6px 12px', borderRadius: '6px',
                              background: 'linear-gradient(135deg, #F59E0B, #F97316)',
                              color: '#fff', fontSize: '12px', fontWeight: 600,
                              textDecoration: 'none', flexShrink: 0,
                              transition: 'opacity 0.2s ease',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.92'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                          >
                            {u.action_box_button_text || 'Atualizar'}
                            <ExternalLink size={11} />
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer (super admin) */}
      {isSuperAdmin && updates.length > 0 && (
        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--dp-border-subtle)', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
          <button
            onClick={openCreate}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #0DAEFF, #3B82F6)', color: '#fff', fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--dp-font)', transition: 'opacity 0.2s ease' }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.92'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
          >
            <Plus size={14} /> Nova versão
          </button>
        </div>
      )}

      {modalOpen && (
        <PlatformUpdateModal
          update={editingUpdate}
          onClose={() => { setModalOpen(false); setEditingUpdate(null); }}
          onSaved={handleSaved}
        />
      )}

      {/* Keyframes locais (o app já define dp-spin/dp-pageFadeIn globalmente) */}
      <style>{`
        @keyframes dp-pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); box-shadow: 0 0 0 0 rgba(52,211,153,0.5); }
          50% { opacity: 0.85; transform: scale(1.15); box-shadow: 0 0 0 4px rgba(52,211,153,0); }
        }
      `}</style>
    </div>
  );
};

export default PlatformUpdatesPanel;
